import { useMemo } from "react";
import PortalLayout from "@/components/PortalLayout";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { resolveClientKey, getClientVessels, MOCK_VESSELS } from "@/data/portalMockData";
import { Download, Eye, Clock } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { usePortalCompany } from "@/hooks/usePortalCompany";

const reports = [
  { title: "Fleet Utilization Report", description: "Vessel availability, uptime, and utilization metrics with trend analysis.", lastGenerated: "2025-03-23 08:00" },
  { title: "HSEQ Monthly Summary", description: "Safety KPIs, incident summary, and compliance status snapshot.", lastGenerated: "2025-03-20 06:00" },
  { title: "Voyage Activity Report", description: "Complete voyage log with passenger counts, fuel consumption, and satisfaction scores.", lastGenerated: "2025-03-24 12:00" },
  { title: "Client Satisfaction Report", description: "NPS trends, survey responses, and per-vessel satisfaction breakdown.", lastGenerated: "2025-03-22 10:00" },
  { title: "Fuel & Emissions Report", description: "Monthly CO₂ emissions, fuel efficiency rankings, and ESG metrics.", lastGenerated: "2025-03-21 09:00" },
  { title: "Financial Summary", description: "Invoice summary, outstanding balances, and day-rate ledger overview.", lastGenerated: "2025-03-23 07:00" },
];

const heatmapMonths = ["Mar 1", "Mar 2", "Mar 3", "Mar 4", "Mar 5", "Mar 6", "Mar 7", "Mar 8", "Mar 9", "Mar 10", "Mar 11", "Mar 12", "Mar 13", "Mar 14"];

const heatmapColors: Record<string, string> = {
  on_hire: "bg-[#0066CC]",
  off_hire: "bg-[#DC3545]",
  maintenance: "bg-[#FFC107]",
  reduced_rate: "bg-[#FD7E14]",
  downtime: "bg-gray-300",
};

const generateHeatmapForVessel = (vesselName: string): string[] => {
  // All vessels showing strong utilization
  return heatmapMonths.map(() => "on_hire");
};

const ReportsAnalytics = () => {
  const { companyName } = usePortalCompany();
  const clientKey = resolveClientKey(companyName);
  const clientVessels = useMemo(() => clientKey ? getClientVessels(clientKey) : MOCK_VESSELS, [clientKey]);

  const heatmapData = useMemo(() => {
    const data: Record<string, string[]> = {};
    clientVessels.forEach(v => {
      data[v.name] = generateHeatmapForVessel(v.name);
    });
    return data;
  }, [clientVessels]);

  return (
    <PortalLayout>
      <div className="mb-6">
        <p className="text-[11px] font-bold tracking-[3px] uppercase text-[hsl(211,100%,40%)]/60 mb-1">Insights</p>
        <h1 className="text-3xl font-bold text-[hsl(215,25%,17%)]">Reports & Analytics</h1>
      </div>

      {/* Report Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {reports.map(r => (
          <div key={r.title} className="portal-card p-5 flex flex-col">
            <h3 className="font-bold text-sm mb-2">{r.title}</h3>
            <p className="text-xs text-[hsl(215,12%,50%)] mb-4 flex-1">{r.description}</p>
            <div className="flex items-center gap-1.5 text-[9px] text-[hsl(215,12%,50%)] mb-3">
              <Clock className="w-3 h-3" /> Last generated: {r.lastGenerated}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => toast({ title: "Preview loading..." })}><Eye className="w-3 h-3 mr-1" /> Preview</Button>
              <Button variant="outline" size="sm" onClick={() => toast({ title: "Report downloading..." })}><Download className="w-3 h-3 mr-1" /> PDF</Button>
            </div>
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[hsl(220,15%,90%)]">
              <Switch /><span className="text-[10px] text-[hsl(215,12%,50%)]">Email monthly</span>
            </div>
          </div>
        ))}
      </div>

      {/* Utilization Heatmap */}
      <div className="portal-card p-5">
        <h3 className="font-bold text-sm mb-4">Vessel Utilization Heatmap — March 2025</h3>
        <div className="overflow-x-auto">
          <div className="min-w-[600px]">
            <div className="flex items-center gap-1 mb-1 pl-[140px]">
              {heatmapMonths.map(d => <div key={d} className="w-8 text-center text-[8px] text-[hsl(215,12%,50%)]">{d.replace("Mar ", "")}</div>)}
            </div>
            {Object.entries(heatmapData).map(([vessel, days]) => (
              <div key={vessel} className="flex items-center gap-1 mb-1">
                <div className="w-[140px] text-xs font-medium text-[hsl(215,25%,17%)] truncate">{vessel}</div>
                {days.map((status, i) => (
                  <div key={i} className={`w-8 h-6 rounded-sm ${heatmapColors[status] || "bg-gray-200"}`} title={`${vessel} - ${heatmapMonths[i]}: ${status.replace("_", " ")}`} />
                ))}
              </div>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-4 mt-3 text-[9px]">
          {Object.entries({ on_hire: "On Hire", off_hire: "Off Hire", maintenance: "Maintenance", reduced_rate: "Reduced Rate", downtime: "Downtime" }).map(([k, v]) => (
            <div key={k} className="flex items-center gap-1"><span className={`w-3 h-3 rounded-sm ${heatmapColors[k]}`} />{v}</div>
          ))}
        </div>
      </div>
    </PortalLayout>
  );
};

export default ReportsAnalytics;
